/* eslint-disable @typescript-eslint/no-explicit-any */

import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Params,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '../service/auth.service';
import { PortfolioApiService } from 'src/app/shared/services/customer-api/portfolio-api.service';
import { ConfigService } from 'src/app/service/config.service';

/**
 * Resolves the active portfolio for portfolio-scoped routes. Reads URL params
 * (?id / ?name / ?portfolioId), falls back to the alphabetically-first folio,
 * and writes the result via PortfolioApiService.setActivePortfolio.
 */
@Injectable()
export class FolioGuard {
  constructor(
    private auth: AuthService,
    private portfolioApi: PortfolioApiService,
    public config: ConfigService,
    private router: Router,
  ) {}

  canActivate(
    _next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> | boolean {
    const currentUrl = state.url.split('?')[0];

    if (
      currentUrl === '/cockpit/news/portfolio/scanner' &&
      this.auth.featureFlags.defaultPortfolioView !== 'scanner'
    ) {
      this.router.navigate(
        [
          '/cockpit/news/portfolio/' +
            this.auth.featureFlags.defaultPortfolioView,
        ],
        { replaceUrl: true },
      );
      return false;
    }

    return this.portfolioApi.portFolios().pipe(
      map((folios) => {
        this.resolveActiveFolio(folios, state.root.queryParams, currentUrl);
        return true;
      }),
    );
  }

  private resolveActiveFolio(
    folios: any[],
    queryParams: Params,
    currentUrl: string,
  ): void {
    if (!folios?.length) {
      this.portfolioApi.setActivePortfolio(null);
      return;
    }

    const { id, name, portfolioId } = queryParams;

    if (!id && name) {
      this.portfolioApi.setActivePortfolio({
        email: null,
        uid: null,
        name,
        content: (queryParams.entities ?? '').split(','),
      });
      return;
    }

    const targetUid = portfolioId ?? id;
    const target = targetUid
      ? folios.find((f) => f.uid === targetUid)
      : undefined;

    if (target) {
      this.portfolioApi.setActivePortfolio(target);
      if (portfolioId && !id) {
        this.router.navigate([currentUrl], {
          queryParams: this.buildPreservedQueryParams(queryParams, {
            id: target.uid,
            portfolioId: null,
          }),
        });
      }
      return;
    }

    if (portfolioId) {
      alert(
        'The link includes a portfolio that is not accessible. You will be redirected to your cockpit.',
      );
    }
    const fallback = this.pickDefaultFolio(folios);
    this.portfolioApi.setActivePortfolio(fallback);
    const overrides = portfolioId
      ? { portfolioId: fallback.uid, id: null, type: queryParams.type }
      : { id: fallback.uid };
    this.router.navigate([currentUrl], {
      queryParams: this.buildPreservedQueryParams(queryParams, overrides),
    });
  }

  private pickDefaultFolio(folios: any[]): any {
    return folios.slice().sort((a, b) => a.name.localeCompare(b.name))[0];
  }

  private buildPreservedQueryParams(
    source: Params,
    overrides: Record<string, string | null | undefined>,
  ): Params {
    const defaultSort =
      this.auth.scorelabSettings.defaultScore === 'sentiment'
        ? '-senti'
        : 'score';
    return {
      news: source.news,
      highlight: source.highlight,
      time: source.time,
      filter: source.filter,
      lang: source.lang ? source.lang : 'en,de',
      feed: source.feed
        ? source.feed
        : 'non_professional,online,print,professional',
      ranks: source.ranks,
      panels: source.panels,
      categories: source.categories ? source.categories : '',
      continents: source.continents,
      countries: source.countries,
      factuality: source.factuality,
      temporality: source.temporality,
      event_ids: source.event_ids,
      sort: defaultSort,
      context: source.context,
      ...overrides,
    };
  }
}
